import {EMPTY, Observable} from 'rxjs';
import moment from 'moment-timezone';
import i18next from 'i18next';
import {List} from 'immutable';

import {combineEpics} from 'web-app/util/redux-observable';
import {getApolloClient} from 'web-app/api/clients/famly-app';
import * as PublicFundingSelectors from 'web-app/react/modals-container/choose-public-funding-modal/selectors';
import * as PublicFundingModalActions from 'web-app/react/modals-container/choose-public-funding-modal/actions';
import * as SessionObservables from 'web-app/react/entities/sessions/epics';
import * as ProductObservables from 'web-app/react/entities/products/epics';
import * as InstitutionObservables from 'web-app/react/entities/institutions/epics';
import {fetchChildPlansData} from 'web-app/react/profile/child-profile/plans-and-invoices/actions';
import {showDeleteDialogObservable, DialogChoice} from 'web-app/react/dialog';
import {getBehaviorsCollectionFromGraphQL} from 'web-app/behaviors/behaviors-collection';
import {mergeUnique} from 'web-app/util/array';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import {isNumber} from 'web-app/util/parsers';
import * as PlanConstants from 'web-app/react/entities/plans/constants';
import * as PricingGroupQueries from 'web-app/react/plans/manage-pricing-groups/queries';
import * as AttendanceScheduleQueries from 'web-app/react/invoicing-profiles/attendance-schedules/queries';
import * as WeekdayBookingSelectors from 'web-app/react/plans/manage-plan/weekdays/weekday-bookings/selectors';
import * as WeekdayEditSelectors from 'web-app/react/plans/manage-plan/edit-weekdays/selectors';

import * as Api from './api';
import * as Actions from './actions';
import * as PlanPricingActions from './plan-pricing/actions';
import * as EditWeekdayActions from './edit-weekdays/actions';
import * as Selectors from './selectors';
import * as SharedSelectors from './shared-selectors';
import * as Queries from './queries';
import TypedEpics from './typed-epics';
import * as EditWeekdaysSelectors from './edit-weekdays/selectors';

const fetchPricingGroups = (institutionId, pricingGroupId) =>
    Observable.fromPromise(
        getApolloClient().query({
            query: PricingGroupQueries.GetNewAndOldPricingGroups,
            fetchPolicy: 'network-only',
            variables: {
                institutionSetId: institutionId,
                pricingGroupIds: pricingGroupId ? [pricingGroupId] : [], // Catches empty string case
            },
        }),
    ).map(({data}) => {
        const pricingGroups = mergeUnique(
            data.finance.pricingGroups.list,
            data.finance.oldPricingGroups.list,
            pricingGroup => pricingGroup.pricingGroupId,
        );

        return {
            behaviors: getBehaviorsCollectionFromGraphQL(data.finance.pricingGroups.behaviors),
            pricingGroups: List(pricingGroups),
        };
    });

const fetchBillingProfiles = (institutionId, billingProfileIds) =>
    Observable.fromPromise(
        getApolloClient().query({
            query: Queries.GetBillingProfilesForPlan,
            fetchPolicy: 'network-only',
            variables: {
                institutionId,
                billingProfileIds,
            },
        }),
    ).map(({data}) => {
        const billingProfiles = mergeUnique(
            data.finance.billingProfiles.list,
            data.finance.oldBillingProfiles.list,
            billingProfile => billingProfile.billingProfileId,
        );

        return {
            billingProfiles: List(billingProfiles),
        };
    });

const fetchAttendanceSchedules = institutionId =>
    Observable.fromPromise(
        getApolloClient().query({
            query: AttendanceScheduleQueries.AttendanceScheduleList,
            fetchPolicy: 'network-only',
            variables: {
                institutionIds: [institutionId],
                includeDeleted: false,
            },
        }),
    ).map(({data}) => {
        return {
            attendanceSchedules: List(data.finance.attendanceSchedules.list),
        };
    });

const previewNewPlanObservable = plan => {
    return Api.createPlan(plan, true).map(planData => Actions.previewPlanSuccess.action(planData.plan));
};

const previewOldPlanObservable = (plan, oldId) => {
    return Api.updatePlan(plan, oldId, true).map(planData => Actions.previewPlanSuccess.action(planData.plan));
};

const validToFromValidOn = validOn => {
    return moment(validOn, 'YYYY-MM-DD').add(2, 'months').format('YYYY-MM-DD');
};

const createPlanEpic = action$ =>
    action$.ofType(Actions.createPlan.type).mergeMap(({payload}) =>
        Api.createPlan(payload.plan)
            .flatMap(response => [
                Actions.createPlanSuccess.action(response.plan, payload.institutionId),
                GlobalEventActions.updateSuccess.action(
                    i18next.t('entities.createSuccess', {
                        entityName: i18next.t('entities.names.plan'),
                    }),
                ),
            ])
            .catch(e => Observable.of(Actions.createPlanFailed.action(e))),
    );

const updatePlanEpic = action$ =>
    action$.ofType(Actions.updatePlan.type).mergeMap(({payload}) =>
        Api.updatePlan(payload.plan, payload.oldPlanId)
            .flatMap(response => [
                Actions.updatePlanSuccess.action(response.plan, payload.institutionId),
                GlobalEventActions.updateSuccess.action(
                    i18next.t('entities.updateSuccess', {
                        entityName: i18next.t('entities.names.plan'),
                    }),
                ),
                fetchChildPlansData.action(payload.institutionId, payload.plan.childId),
            ])
            .catch(e => Observable.of(Actions.updatePlanFailed.action(e))),
    );

const deletePlanEpic = action$ =>
    action$.ofType(Actions.deletePlan.type).mergeMap(({payload}) =>
        showDeleteDialogObservable({title: '', description: i18next.t('plans.managePlan.deleteConfirmation')}).mergeMap(
            selection => {
                if (selection === DialogChoice.OK) {
                    return Api.deletePlan(payload.planId)
                        .flatMap(() => [
                            Actions.deletePlanSuccess.action({institutionId: payload.institutionId}),
                            Actions.closeManagePlan.action(),
                            GlobalEventActions.updateSuccess.action(
                                i18next.t('entities.deleteSuccess', {
                                    entityName: i18next.t('entities.names.plan'),
                                }),
                            ),
                            fetchChildPlansData.action(payload.institutionId, payload.childId),
                        ])
                        .catch(e => Observable.of(Actions.deletePlanFailed.action(e)));
                } else {
                    return Observable.of(Actions.deletePlanAborted.action());
                }
            },
        ),
    );

const fetchManagePlanDataObservable = (institutionId, validOn, pricingGroupId, billingProfileIds) => {
    const observables = [
        fetchPricingGroups(institutionId, pricingGroupId),
        fetchBillingProfiles(institutionId, billingProfileIds),
        fetchAttendanceSchedules(institutionId),
        SessionObservables.fetchAllObservable(institutionId, validOn, validToFromValidOn(validOn), pricingGroupId),
        InstitutionObservables.fetchOpeningHoursObservable(institutionId),
        ProductObservables.fetchAllObservable(institutionId, validOn, validToFromValidOn(validOn), pricingGroupId),
    ].filter(o => Boolean(o));

    return Observable.combineLatest(...observables)
        .flatMap(
            ([pricingGroupsData, billingProfilesData, attendanceSchedulesData, sessionAction, ...successActions]) => {
                return [sessionAction, ...successActions].concat([
                    Actions.fetchManagePlanDataSuccess.action(
                        sessionAction.payload.sessions,
                        pricingGroupsData.pricingGroups,
                        pricingGroupsData.behaviors,
                        billingProfilesData.billingProfiles,
                        attendanceSchedulesData.attendanceSchedules,
                    ),
                ]);
            },
        )
        .catch(e => Observable.of(Actions.fetchManagePlanDataFailed.action(e)));
};

const fetchManagePlanDataEpic = action$ =>
    action$.ofType(Actions.fetchManagePlanData.type).mergeMap(({payload}) => {
        const {institutionId, validOn, pricingGroupId, billingProfileIds} = payload;

        return fetchManagePlanDataObservable(institutionId, validOn, pricingGroupId, billingProfileIds);
    });

const fetchManagePlanDataFromState = (action$, state$) =>
    action$
        .ofType(
            Actions.planPeriodDidChange.type,
            Actions.pricingGroupDidChange.type,
            Actions.updateToLaterPriceChange.type,
        )
        .mergeMap(() => {
            const {institutionId, validOn, pricingGroupId, billingProfileIds} = SharedSelectors.managePlanDataVariables(
                state$.value,
            );

            return Observable.merge(
                Observable.of(Actions.startFetchingData.action()),
                fetchManagePlanDataObservable(institutionId, validOn, pricingGroupId, billingProfileIds),
            );
        });

/**
 * Only run when plan is invalid.
 *
 * Check if any of the sessions and products are not available for the given day making the plan invalid.
 * When we are sure the plan is valid save the preview.
 */
const refreshAfterInvalidPlanIsUpdatedEpic = (action$, state$) =>
    action$
        .ofType(EditWeekdayActions.productBookingChanged.type, EditWeekdayActions.sessionBookingChanged.type)
        .mergeMap(() => {
            const state = state$.value;
            const isInvalid = Selectors.isInvalidPlan(state);

            if (!isInvalid) {
                return EMPTY;
            }

            const getAvailableWeekdayProducts = WeekdayBookingSelectors.makeGetAvailableWeekdayProducts();
            const getAvailableWeekdaySessions = WeekdayEditSelectors.makeGetAvailableWeekdaySessions();

            const day = EditWeekdaysSelectors.selectDayInEditor(state);

            const currentEditedDay = EditWeekdaysSelectors.editDay(state);

            const availableSessions = getAvailableWeekdaySessions(state, {day});
            const noUnavailableSessions = !currentEditedDay.sessionBookings.some(
                booking =>
                    !availableSessions.some(availableSession => availableSession.sessionId === booking.sessionId),
            );

            const availableProducts = getAvailableWeekdayProducts(state, {day});
            const noUnavailableProducts = !currentEditedDay.productBookings.some(
                booking =>
                    !availableProducts.some(availableProduct => availableProduct.productId === booking.productId),
            );

            if (noUnavailableProducts && noUnavailableSessions) {
                const planPart = state.plans.managePlan.editWeekdays.planPart;
                return Observable.of(EditWeekdayActions.savePlanWeekday.action(planPart));
            } else {
                return EMPTY;
            }
        });

/**
 * This function is only used to validate the values
 * typed into the form in the ChoosePublicFunding modal.
 *
 * It will prevent sending off a request to the plan preview endpoint that we know will fail.
 */
const isPublicFundingSetupValid = publicFundingSetup => {
    if (!publicFundingSetup) {
        return false;
    }

    if (publicFundingSetup.fundingMethod === PlanConstants.PUBLIC_FUNDING_TYPE.NONE) {
        return true;
    }

    if (publicFundingSetup.fundingMethod === PlanConstants.PUBLIC_FUNDING_TYPE.UNKNOWN) {
        return false;
    }

    if (!isNumber(publicFundingSetup.hours) && !isNumber(publicFundingSetup.minutes)) {
        return false;
    }

    if (publicFundingSetup.fundingMethod === PlanConstants.PUBLIC_FUNDING_TYPE.MANUAL_BY_RATE) {
        return isNumber(publicFundingSetup.rate);
    }

    return true;
};

const previewPlanPublicFundingEpic = (action$, state$) =>
    Observable.merge(
        action$.ofType(Actions.previewPlanPublicFunding.type),
        action$.ofType(PublicFundingModalActions.changeFundingMethod.type),
    )
        .debounceTime(250)
        .mergeMap(() => {
            const state = state$.value;

            const publicFundingValues = PublicFundingSelectors.publicFundingValues(state);

            // Actually, the public funding settings are validated in `Selectors.isInvalidPlan`,
            // however, it's using a part of the state that isn't always available. Instead we
            // create a separate validation on the `publicFundingValues` which come directly from
            // the public funding modal.
            if (Selectors.isInvalidPlan(state) || !isPublicFundingSetupValid(publicFundingValues)) {
                return Observable.of(Actions.previewPlanPublicFundingAborted.action());
            }

            const plan = PublicFundingSelectors.publicFundingPlan(state);
            const planId = SharedSelectors.editPlanId(state);
            const previewObservable = planId ? Api.updatePlan(plan, planId, true) : Api.createPlan(plan, true);
            return previewObservable
                .map(planData => Actions.previewPlanPublicFundingSuccess.action(planData.plan))
                .catch(e => Observable.of(Actions.previewPlanPublicFundingFailed.action(e)));
        });

const DEBOUNCE_PLAN_PREVIEW_INTERVAL = 250;
const previewPlanEpic = (action$, state$) => {
    const listenToPlanChanges = Observable.merge(
        action$.ofType(Actions.fetchManagePlanDataSuccess.type),
        action$.ofType(Actions.showEdit.type),
        action$.ofType(Actions.updateToLaterPriceChange.type),
        action$.ofType(EditWeekdayActions.savePlanWeekday.type),
        action$.ofType(Actions.addPlanPart.type),
        action$.ofType(Actions.removePlanPart.type),
        action$.ofType(Actions.removeBooking.type),
        action$.ofType(Actions.removeProductBooking.type),
        action$.ofType(Actions.removeAllBookings.type),
        action$.ofType(Actions.toggleFundable.type),
        action$.ofType(Actions.packageDidChange.type),
        action$.ofType(Actions.planPeriodDidChange.type),
        action$.ofType(Actions.pricingGroupDidChange.type),
        action$.ofType(Actions.setBillingProfile.type),
        action$.ofType(Actions.packagesDidChange.type),
        action$.ofType(PlanPricingActions.removePublicFunding.type),
        action$.ofType(PublicFundingModalActions.save.type),
        // This action is both for discounts and surcharge changes
        action$.ofType(PlanPricingActions.addDiscount.type),
        action$.ofType(PlanPricingActions.convertDiscountOrigin.type),
        action$.ofType(PlanPricingActions.updateDiscountPreset.type),
        action$.ofType(PlanPricingActions.discountDidChange.type),
        action$.ofType(PlanPricingActions.convertToPercentage.type),
        action$.ofType(PlanPricingActions.discountFrequencyChanged.type),
        action$.ofType(PlanPricingActions.removeDiscount.type),
    );

    const doFetchPreview = listenToPlanChanges.debounceTime(DEBOUNCE_PLAN_PREVIEW_INTERVAL).mergeMap(() => {
        const state = state$.value;
        if (state.plans.managePlan.fetchingData || Selectors.isInvalidPlan(state)) {
            return Observable.of(Actions.previewPlanAborted.action());
        }
        const plan = Selectors.previewPlan(state);
        const planId = SharedSelectors.editPlanId(state);
        const previewObservable = planId ? previewOldPlanObservable(plan, planId) : previewNewPlanObservable(plan);
        return previewObservable
            .takeUntil(listenToPlanChanges)
            .catch(e => Observable.of(Actions.previewPlanFailed.action(e)));
    });

    const startPreviewObservable = listenToPlanChanges.map(() => Actions.startPreviewPlan.action());

    return Observable.merge(startPreviewObservable, doFetchPreview);
};

export default combineEpics(
    createPlanEpic,
    updatePlanEpic,
    deletePlanEpic,
    fetchManagePlanDataEpic,
    fetchManagePlanDataFromState,
    previewPlanPublicFundingEpic,
    previewPlanEpic,
    refreshAfterInvalidPlanIsUpdatedEpic,
    TypedEpics,
);
